<template>
  <div
    class="search-bar flex-1"
    @click="clickedOnSearchBar"
  >
    <div class="flex items-start">
      <div
        v-click-outside="closeSearchResults"
        class="w-full"
      >
        <div class="h-11 w-full rounded-md flex justify-center relative">
          <label
            for="search-input"
            class="sr-only"
          >
            {{ $t('Site search') }}
          </label>
          <input
            id="search-input"
            ref="searchInput"
            type="text"
            :value="currentValue.name"
            :maxlength="MAX_LENGTH_FOR_SEARCH_INPUT"
            :placeholder="$t('search-bar.input-placeholder')"
            data-testid="search-bar__input-placeholder"
            class="bg-EXPONDO-white h-full rounded pl-3 pr-20 text-EXPONDO-black w-full relative
                    border border-solid border-EXPONDO-primary-20 placeholder:text-EXPONDO-grey-60"
            @focus="openSearchResults()"
            @input="updateSearch(($event.currentTarget as HTMLInputElement).value)"
            @keyup.enter="onSearch()"
            @keyup.down="onArrowDown(hits)"
            @keyup.up="onArrowUp(hits)"
          >
          <button
            v-if="currentValue.name && isSearchResultsOpen"
            data-testid="searchBarClearInput"
            class="right-14 top-3 absolute"
            @click="updateSearch('')"
          >
            <span class="iconify svg--close text-EXPONDO-primary-60 pr-0.5" />
          </button>

          <button
            class="absolute top-0 right-0 w-12 rounded-r h-full flex justify-center items-center"
            data-testid="search-bar__show-results"
            :aria-label="$t('Submit search')"
            @click="onSearch()"
          >
            <span class="iconify svg--search text-lg text-EXPONDO-primary" />
          </button>
        </div>
        <div
          v-if="currentValue.name && isSearchResultsOpen"
          class="rounded shadow-lg bg-EXPONDO-white relative z-40"
        >
          <template v-if="isSkuSearchActive">
            <div class="p-6 py-4 text-EXPONDO-black">
              <SearchBarResults
                v-if="skuResult"
                data-testid="search-bar__sku-result"
                :hits="[skuResult]"
                :active-index="activeIndex"
                @product-clicked="onSkuResultClicked"
              />
              <span
                v-else
                data-testid="search-bar__sku-no-result"
              >{{ $t('No results') }}</span>
            </div>
          </template>
          <template v-else>
            <div class="p-6 py-4 text-EXPONDO-black">
              <transition name="sf-fade">
                <div
                  v-if="hits.length > 0"
                  key="results"
                >
                  <SearchBarResults
                    :hits="hits"
                    :active-index="activeIndex"
                    @product-clicked="hit => onProductClicked(hit)"
                    @search-results-shown="onSearchResultsShown(hits)"
                  />
                  <button
                    v-if="hits.length >= ITEM_COUNT_FOR_QUICK_SEARCH"
                    class="text-EXPONDO-primary mt-2 flex items-center mx-auto"
                    @click="onSearch()"
                  >
                    {{ $t('See all results') }}
                    <span class="iconify svg--chevron-right text-EXPONDO-primary text-xs ml-4" />
                  </button>
                </div>
                <span
                  v-else-if="loading"
                  key="loading"
                >{{ $t('Searching...') }}</span>
                <span
                  v-else
                  key="no-results"
                >{{ $t('No results') }}</span>
              </transition>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// CT version of SearchBar.vue, without InstantSearch; SearchBarResults gets the hits as a prop.
import type { HitResultItem } from 'vue-instantsearch/vue3/es';
import {
  ITEM_COUNT_FOR_QUICK_SEARCH,
  MIN_CHARS_FOR_SEARCH,
  MAX_LENGTH_FOR_SEARCH_INPUT,
} from '~~/constants/typesense';
import { ROUTES } from '~~/constants/routes';
import { isSkuSearch } from '~~/helpers/search/isSkuSearch';
import { addHitPositions } from '~~/helpers/productListing/addHitPositions';

interface Props {
  languageAndCountry: string,
}

defineProps<Props>();
const emit = defineEmits<{
    (e: 'product-clicked', payload: { product: HitResultItem; searchTerm: string }): void;
    (e: 'search', searchTerm: string): void;
    (e: 'search-bar-clicked'): void;
    (e: 'search-results-shown', products: HitResultItem[]): void;
  }>();

const { getAdjustedSlug } = useRouteExtended();
const router = useRouter();
const searchInput = ref<HTMLElement | null>(null);

const { skuResult, search: searchBySku, clear: clearSkuResult } = useSkuSearch();
const isSkuSearchActive = ref<boolean>(false);

const {
  search: searchAutocomplete,
  clear: clearAutocomplete,
  hits: autocompleteHits,
  loading,
} = useCtAutocompleteSearch();
const hits = computed<HitResultItem[]>(() => addHitPositions(autocompleteHits.value));

const DEFAULT_ACTIVE_INDEX: number = -1;
const activeIndex = ref<number>(DEFAULT_ACTIVE_INDEX);
const currentValue = ref<{ name: string; url: string; }>({ name: '', url: '' });
const isSearchResultsOpen = ref<boolean>(false);

const updateSearch = (value: string) => {

  if (!value) {

    currentValue.value = { name: '', url: '' };
    clearAutocomplete();
    closeSearchResults();
    clearSkuResult();
    isSkuSearchActive.value = false;

    if (searchInput.value instanceof HTMLElement) {

      searchInput.value.focus();

    }

    return;

  }

  if (isSkuSearch(value)) {

    currentValue.value = { name: value, url: '' };
    isSkuSearchActive.value = true;
    openSearchResults();
    searchBySku(value);

    return;

  }

  isSkuSearchActive.value = false;
  clearSkuResult();

  if (value.length < MIN_CHARS_FOR_SEARCH) {

    return;

  }

  searchAutocomplete(value, ITEM_COUNT_FOR_QUICK_SEARCH);
  openSearchResults();
  currentValue.value = { name: value, url: '' };

};

const onSearch = () => {

  if (isSkuSearchActive.value) {

    closeSearchResults();

    if (skuResult.value) {

      router.push(skuResult.value.url);

      return;

    }

    const searchTerm = currentValue.value.name?.trim() || '';

    router.push({ path: getAdjustedSlug(ROUTES.SEARCH), query: { query: searchTerm } });

    return;

  }

  const searchTerm = currentValue.value.name?.trim() || '';

  emit('search', searchTerm);

  closeSearchResults();

  if (currentValue.value.url) {

    router.push(currentValue.value.url);

    return;

  }

  const hitsPerPageParam = router.currentRoute.value.query?.hitsPerPage;
  const hitsPerPage = Array.isArray(hitsPerPageParam)
    ? hitsPerPageParam[0]
    : hitsPerPageParam ?? '';

  const queryParams: Record<string, string> = {};

  if (typeof hitsPerPage === 'string' && hitsPerPage.length) {

    queryParams.hitsPerPage = hitsPerPage;

  }

  queryParams.query = searchTerm || '';
  router.push({
    path: getAdjustedSlug(ROUTES.SEARCH),
    query: queryParams,
  });

};

const setCurrentValue = (value: HitResultItem) => {

  currentValue.value = { name: value.name || '', url: value.url || '' };

};

const onArrowUp = (navigableHits: HitResultItem[]) => {

  if (activeIndex.value < 1) {

    activeIndex.value = navigableHits.length - 1;

  } else {

    activeIndex.value--;
    setCurrentValue(navigableHits[activeIndex.value]);

  }

};

const onArrowDown = (navigableHits: HitResultItem[]) => {

  if (activeIndex.value + 1 < navigableHits.length) {

    activeIndex.value++;
    setCurrentValue(navigableHits[activeIndex.value]);

  } else {

    activeIndex.value = 0;

  }

};

const openSearchResults = () => {

  isSearchResultsOpen.value = true;

};

const closeSearchResults = () => {

  isSearchResultsOpen.value = false;

};

const onProductClicked = (product: HitResultItem) => {

  closeSearchResults();
  emit('product-clicked', { product, searchTerm: currentValue.value.name });

};

const onSkuResultClicked = () => {

  closeSearchResults();

};

const onSearchResultsShown = (products: HitResultItem[]) => {

  emit('search-results-shown', products);

};

setCurrentValue({ name: '', url: '' });

const clickedOnSearchBar = () => {

  emit('search-bar-clicked');

};
</script>
